<template>
  <div class="tts-page">
    <div class="tts-container">
      <el-row :gutter="20" class="full-height-row">
      <!-- 左侧：音色列表 -->
      <el-col :span="6" class="full-height-col">
        <el-card class="speaker-list-card">
          <div slot="header" class="card-header-flex">
            <span>音色库</span>
            <span>
              <el-button type="primary" size="mini" icon="el-icon-plus" @click="showAddDialog = true">新增音色</el-button>
              <el-radio-group v-model="genderFilter" size="mini" style="margin-left: 8px;">
                <el-radio-button label="all">全部</el-radio-button>
                <el-radio-button label="female">女</el-radio-button>
                <el-radio-button label="male">男</el-radio-button>
              </el-radio-group>
            </span>
          </div>
          <div v-loading="loadingSpeakers" class="speaker-list">
            <div
              v-for="speaker in filteredSpeakers"
              :key="speaker.id"
              :class="['speaker-item', { active: selectedSpeaker && selectedSpeaker.id === speaker.id }]"
              @click="handleSelectSpeaker(speaker)"
            >
              <div class="speaker-info">
                <div class="speaker-name">{{ speaker.name }}</div>
                <div class="speaker-id">{{ speaker.id }}</div>
                <div v-if="speaker.used_by_characters && speaker.used_by_characters.length > 0" class="speaker-used-by">
                  <el-tag
                    v-for="charName in speaker.used_by_characters"
                    :key="charName"
                    size="mini"
                    type="success"
                    class="used-by-tag"
                  >
                    {{ charName }}
                  </el-tag>
                </div>
              </div>
              <div class="speaker-actions">
                <el-button
                  type="text"
                  icon="el-icon-video-play"
                  circle
                  :loading="auditioningId === speaker.id"
                  @click.stop="handleQuickAudition(speaker)"
                ></el-button>
                <el-button
                  v-if="speaker.is_custom"
                  type="text"
                  icon="el-icon-delete"
                  circle
                  style="color: #F56C6C; margin-left: 2px;"
                  @click.stop="handleDeleteSpeaker(speaker)"
                ></el-button>
                <el-tag size="mini" :type="speaker.gender === 'female' ? 'danger' : 'primary'">
                  {{ speaker.gender === 'female' ? '女' : '男' }}
                </el-tag>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- 右侧：主操作区与历史记录 -->
      <el-col :span="18" class="main-content-col">
        <div class="right-scroll-area">
          <!-- 合成面板 -->
          <el-card class="synth-panel" header="语音合成配置">
            <el-form :model="synthForm" label-width="100px">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="当前音色">
                    <el-tag type="info" effect="plain" style="width: 100%; text-align: center;">
                      {{ selectedSpeaker ? selectedSpeaker.name : '未选择' }}
                    </el-tag>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="选择语气">
                    <el-select
                      v-model="synthForm.emotion"
                      :disabled="!selectedSpeaker"
                      filterable
                      allow-create
                      default-first-option
                      style="width: 100%"
                    >
                      <el-option
                        v-for="emo in supportedEmotions"
                        :key="emo"
                        :label="emotionMap[emo] || emo"
                        :value="emo"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="语气强度">
                <el-slider
                  v-model="synthForm.emotion_scale"
                  :min="0.5"
                  :max="2.0"
                  :step="0.1"
                  :disabled="!selectedSpeaker"
                  show-input
                />
              </el-form-item>

              <el-form-item label="语速调节">
                <el-slider
                  v-model="synthForm.speech_rate"
                  :min="-50"
                  :max="100"
                  :step="1"
                  :disabled="!selectedSpeaker"
                  :format-tooltip="formatSpeechRateTooltip"
                  show-input
                />
              </el-form-item>

              <el-form-item label="待合成文本">
                <el-input
                  v-model="synthForm.text"
                  type="textarea"
                  :rows="4"
                  placeholder="请输入要合成的文本..."
                  :disabled="!selectedSpeaker"
                />
              </el-form-item>

              <el-form-item>
                <el-button
                  type="primary"
                  :loading="synthesizing"
                  :disabled="!canSynthesize"
                  icon="el-icon-cpu"
                  @click="handleSynthesize"
                >
                  开始生成语音
                </el-button>
                <el-button @click="resetForm">重置文案</el-button>
              </el-form-item>
            </el-form>

            <!-- 播放器区域 -->
            <div v-if="audioUrl" class="audio-player-wrap">
              <div class="audio-header">最新生成结果：</div>
              <div class="result-info">
                <span class="result-tag"><strong>音色:</strong> {{ resultInfo.speaker }}</span>
                <span class="result-tag"><strong>语气:</strong> {{ emotionMap[resultInfo.emotion] || resultInfo.emotion }} (x{{ resultInfo.emotion_scale }})</span>
                <span class="result-tag"><strong>语速:</strong> {{ formatSpeechRateTooltip(resultInfo.speech_rate || 0) }}</span>
                <div class="result-text"><strong>文本:</strong> {{ resultInfo.text }}</div>
              </div>
              <audio ref="audioPlayer" :src="audioUrl" controls autoplay class="full-audio-player"></audio>
            </div>
          </el-card>

          <!-- 历史记录 -->
          <el-card class="history-card" header="合成历史记录">
            <el-table :data="history" style="width: 100%" size="small">
              <el-table-column label="时间" width="90">
                <template slot-scope="scope">
                  <span style="color: #909399">{{ formatTime(scope.row.time) }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="speaker" label="音色" width="110" />
              <el-table-column label="语气/语速" width="180">
                <template slot-scope="scope">
                  <el-tag size="mini" type="info">{{ emotionMap[scope.row.emotion] || scope.row.emotion }}</el-tag>
                  <span style="font-size: 11px; margin-left: 4px;">x{{ scope.row.emotion_scale }}</span>
                  <span style="font-size: 11px; margin-left: 8px; color: #E6A23C;">
                    {{ formatSpeechRateTooltip(scope.row.speech_rate || 0) }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="text" label="文案内容" show-overflow-tooltip />
              <el-table-column label="操作" width="220" fixed="right">
                <template slot-scope="scope">
                  <el-button type="text" size="mini" icon="el-icon-refresh" @click="syncToConfig(scope.row)">同步到配置</el-button>
                  <el-button type="text" size="mini" icon="el-icon-video-play" @click="playHistory(scope.row)">回放</el-button>
                  <el-button type="text" size="mini" icon="el-icon-download" @click="downloadHistory(scope.row)">下载</el-button>
                  <el-button type="text" size="mini" icon="el-icon-delete" style="color: #F56C6C" @click="deleteHistory(scope.$index)">移除</el-button>
                </template>
              </el-table-column>
            </el-table>
            <div v-if="history.length > 0" style="margin-top: 15px; text-align: right;">
              <el-button type="text" size="mini" icon="el-icon-delete" @click="clearHistory">清空所有历史记录</el-button>
            </div>
          </el-card>
        </div>
      </el-col>
    </el-row>
  </div>

  <!-- 新增音色对话框 -->
  <el-dialog title="新增自定义音色" :visible.sync="showAddDialog" width="450px" @closed="resetNewSpeakerForm">
    <el-form :model="newSpeakerForm" label-width="100px" size="small">
      <el-form-item label="音色ID" required>
        <el-input v-model="newSpeakerForm.id" placeholder="例如：S_custom_001 或 custom_voice" />
        <span style="font-size: 11px; color: #999;">S_ 前缀将使用音色克隆模型，其他使用标准 TTS 2.0 模型</span>
      </el-form-item>
      <el-form-item label="音色名称" required>
        <el-input v-model="newSpeakerForm.name" placeholder="例如：我的专属音色" />
      </el-form-item>
      <el-form-item label="性别" required>
        <el-radio-group v-model="newSpeakerForm.gender">
          <el-radio label="female">女</el-radio>
          <el-radio label="male">男</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="示例文本">
        <el-input v-model="newSpeakerForm.sample_text" type="textarea" :rows="3" placeholder="可选，用于试听的示例文本" />
      </el-form-item>
    </el-form>
    <span slot="footer">
      <el-button size="small" @click="showAddDialog = false">取消</el-button>
      <el-button type="primary" size="small" :loading="addingSpeaker" @click="handleAddSpeaker">确认新增</el-button>
    </span>
  </el-dialog>
</div>
</template>

<script>
import { getTTSSpeakers, synthesizeTTS, getTTSSpeakerAudition, createTTSSpeaker, deleteTTSSpeaker } from "@/net/api";
import { EMOTION_MAP } from "@/config/emotions";

export default {
  name: "TTSView",
  data() {
    return {
      speakers: [],
      genderFilter: "all",
      loadingSpeakers: false,
      selectedSpeaker: null,
      synthesizing: false,
      auditioningId: "",
      audioUrl: "",
      resultInfo: {
        speaker: "",
        emotion: "",
        emotion_scale: 1.0,
        speech_rate: 0,
        text: ""
      },
      history: JSON.parse(localStorage.getItem("tts_history") || "[]"),
      synthForm: {
        text: "",
        emotion: "happy",
        emotion_scale: 1.0,
        speech_rate: 0
      },
      showAddDialog: false,
      addingSpeaker: false,
      newSpeakerForm: {
        id: "",
        name: "",
        gender: "female",
        sample_text: ""
      },
      emotionMap: EMOTION_MAP
    };
  },
  computed: {
    filteredSpeakers() {
      if (this.genderFilter === "all") return this.speakers;
      return this.speakers.filter(s => s.gender === this.genderFilter);
    },
    supportedEmotions() {
      return Object.keys(this.emotionMap);
    },
    canSynthesize() {
      return this.selectedSpeaker && this.synthForm.text.trim().length > 0;
    }
  },
  created() {
    this.loadSpeakers();
  },
  methods: {
    async loadSpeakers() {
      this.loadingSpeakers = true;
      try {
        const res = await getTTSSpeakers();
        if (res.data.code === 0) {
          this.speakers = res.data.data;
          if (this.speakers.length > 0) {
            this.handleSelectSpeaker(this.speakers[0]);
          }
        } else {
          this.$message.error(res.data.message || "获取音色列表失败");
        }
      } catch (e) {
        this.$message.error("获取音色列表失败");
      } finally {
        this.loadingSpeakers = false;
      }
    },
    handleSelectSpeaker(speaker) {
      this.selectedSpeaker = speaker;
      if (!this.synthForm.emotion) {
        this.synthForm.emotion = "happy";
      }
      if (!this.synthForm.text) {
        this.synthForm.text = speaker.sample_text || "";
      }
    },
    async handleQuickAudition(speaker) {
      this.auditioningId = speaker.id;
      try {
        const res = await getTTSSpeakerAudition(speaker.id);
        if (res.data.code === 0) {
          this.audioUrl = res.data.data.url;
          this.resultInfo = {
            speaker: speaker.name,
            emotion: "happy",
            emotion_scale: 1.0,
            text: speaker.sample_text
          };
        } else {
          this.$message.error(res.data.message || "试听失败");
        }
      } catch (e) {
        this.$message.error("试听失败");
      } finally {
        this.auditioningId = "";
      }
    },
    async handleSynthesize() {
      this.synthesizing = true;
      try {
        const payload = {
          text: this.synthForm.text,
          speaker_id: this.selectedSpeaker.id,
          emotion: this.synthForm.emotion,
          emotion_scale: this.synthForm.emotion_scale,
          speech_rate: this.synthForm.speech_rate
        };
        const res = await synthesizeTTS(payload);
        if (res.data.code === 0) {
          this.audioUrl = res.data.data.url;
          this.resultInfo = {
            speaker: this.selectedSpeaker.name,
            emotion: this.synthForm.emotion,
            emotion_scale: this.synthForm.emotion_scale,
            speech_rate: this.synthForm.speech_rate,
            text: this.synthForm.text
          };
          
          // 添加到历史记录
          this.history.unshift({
            id: Date.now(),
            time: new Date(),
            speaker_id: this.selectedSpeaker.id,
            speaker: this.selectedSpeaker.name,
            emotion: this.synthForm.emotion,
            emotion_scale: this.synthForm.emotion_scale,
            speech_rate: this.synthForm.speech_rate,
            text: this.synthForm.text,
            url: this.audioUrl
          });
          this.saveHistory();
          
          this.$message.success("合成成功");
        } else {
          this.$message.error(res.data.message || "合成失败");
        }
      } catch (e) {
        this.$message.error(e.response?.data?.message || "合成失败");
      } finally {
        this.synthesizing = false;
      }
    },
    resetForm() {
      this.synthForm.text = this.selectedSpeaker ? this.selectedSpeaker.sample_text : "";
      this.synthForm.emotion = this.supportedEmotions[0] || "happy";
      this.synthForm.emotion_scale = 1.0;
      this.synthForm.speech_rate = 0;
      this.audioUrl = "";
    },
    formatSpeechRateTooltip(val) {
      return (1.0 + val / 100).toFixed(2) + "x";
    },
    downloadAudio() {
      if (!this.audioUrl) return;
      this.downloadFile(this.audioUrl);
    },
    downloadFile(url, name) {
      const link = document.createElement("a");
      link.href = url;
      link.download = name || `tts_${new Date().getTime()}.mp3`;
      link.click();
    },
    saveHistory() {
      localStorage.setItem("tts_history", JSON.stringify(this.history.slice(0, 50))); // 最多保存 50 条
    },
    playHistory(row) {
      this.audioUrl = row.url;
      this.resultInfo = {
        speaker: row.speaker,
        emotion: row.emotion,
        emotion_scale: row.emotion_scale,
        text: row.text
      };
      this.$nextTick(() => {
        this.$refs.audioPlayer?.play();
      });
    },
    syncToConfig(row) {
      // 优先通过 speaker_id 匹配，其次通过 speaker name 匹配
      let speaker = null;
      if (row.speaker_id) {
        speaker = this.speakers.find(s => s.id === row.speaker_id);
      }
      if (!speaker) {
        speaker = this.speakers.find(s => s.name === row.speaker);
      }
      if (!speaker) {
        this.$message.warning(`未找到音色 "${row.speaker}"，请确保音色库已加载`);
        return;
      }
      this.selectedSpeaker = speaker;
      this.synthForm.emotion = row.emotion;
      this.synthForm.emotion_scale = row.emotion_scale;
      this.synthForm.speech_rate = row.speech_rate || 0;
      this.synthForm.text = row.text;
      this.$message.success("已同步到合成配置");
    },
    downloadHistory(row) {
      this.downloadFile(row.url, `tts_${row.speaker}_${row.id}.mp3`);
    },
    deleteHistory(index) {
      this.history.splice(index, 1);
      this.saveHistory();
    },
    clearHistory() {
      this.$confirm("确定清空所有合成历史吗？", "提示", { type: "warning" }).then(() => {
        this.history = [];
        this.saveHistory();
      });
    },
    resetNewSpeakerForm() {
      this.newSpeakerForm = {
        id: "",
        name: "",
        gender: "female",
        sample_text: ""
      };
    },
    async handleAddSpeaker() {
      const form = this.newSpeakerForm;
      if (!form.id.trim()) {
        this.$message.warning("请输入音色ID");
        return;
      }
      if (!form.name.trim()) {
        this.$message.warning("请输入音色名称");
        return;
      }
      this.addingSpeaker = true;
      try {
        const res = await createTTSSpeaker(form);
        if (res.data.code === 0) {
          this.$message.success("新增音色成功");
          this.showAddDialog = false;
          this.loadSpeakers();
        } else {
          this.$message.error(res.data.message || "新增失败");
        }
      } catch (e) {
        this.$message.error(e.response?.data?.message || "新增音色失败");
      } finally {
        this.addingSpeaker = false;
      }
    },
    handleDeleteSpeaker(speaker) {
      this.$confirm(`确定删除自定义音色「${speaker.name}」吗？`, "提示", {
        type: "warning"
      }).then(async () => {
        try {
          const res = await deleteTTSSpeaker(speaker.id);
          if (res.data.code === 0) {
            this.$message.success("删除成功");
            if (this.selectedSpeaker && this.selectedSpeaker.id === speaker.id) {
              this.selectedSpeaker = null;
              this.audioUrl = "";
            }
            this.loadSpeakers();
          } else {
            this.$message.error(res.data.message || "删除失败");
          }
        } catch (e) {
          this.$message.error(e.response?.data?.message || "删除失败");
        }
      }).catch(() => {});
    },
    formatTime(dateStr) {
      const date = new Date(dateStr);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    }
  }
};
</script>

<style scoped>
.tts-container {
  padding: 20px;
  height: calc(100vh - 100px);
  overflow: hidden;
}
.full-height-row {
  height: 100%;
  display: flex;
}
.full-height-col {
  height: 100%;
}
.main-content-col {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.speaker-list-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.speaker-list-card /deep/ .el-card__body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}
.speaker-list-card /deep/ .el-card__header {
  padding: 10px 15px;
}
.card-header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.speaker-list {
  overflow-y: auto;
  flex: 1;
}
.right-scroll-area {
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
}
.speaker-item {
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s;
}
.speaker-item:hover {
  background-color: #f8faff;
}
.speaker-item.active {
  background-color: #f0f7ff;
  border-left: 4px solid #409eff;
}
.speaker-name {
  font-weight: bold;
  font-size: 14px;
  color: #2c3e50;
}
.speaker-id {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}
.speaker-used-by {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.used-by-tag {
  font-size: 10px !important;
}
.synth-panel {
  margin-bottom: 20px;
}
.history-card {
  margin-bottom: 20px;
}
.audio-player-wrap {
  margin-top: 25px;
  padding: 15px 20px;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.audio-header {
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}
.result-info {
  margin-bottom: 15px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}
.result-tag {
  display: inline-block;
  margin-right: 20px;
  font-size: 13px;
  color: #606266;
}
.result-text {
  margin-top: 8px;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}
.full-audio-player {
  width: 100%;
  height: 40px;
}
</style>
